import { gql } from '@apollo/client';
import axios from 'axios';

/**
 * Query to get cloud accounts count for an application.
 * Used to count active cloud accounts before retiring an application.
 */
export const GetCloudAccountsCountQuery = gql`
  query CloudAccountsCount(
    $applicationId: Int!
    $filter: ResourceCatalogCloudAccountsStatusFilter
  ) {
    resources {
      application(applicationId: $applicationId) {
        cloudAccounts(filter: $filter) {
          id
          status
        }
      }
    }
  }
`;

/**
 * Query to get AD groups and role assignments for an application.
 * Used to count/list Active Directory groups and detect group role assignments
 * before allowing deletion.
 */
export const GetAdGroupsCountQuery = gql`
  query AdGroupsCount($applicationId: Int!) {
    resources {
      application(applicationId: $applicationId) {
        resources {
          adGroupsPaged {
            totalCount
            adGroups {
              groupName
              groupId
            }
          }
        }
        roles {
          name
          displayName
          responsibilities {
            responsibilityId
            responsibleParty {
              ... on Group {
                name
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * Mutation to delete all AD groups for an application.
 * Used in the app decom checklist to initiate AD group deletion.
 */
export const DeleteApplicationAdGroupMutation = gql`
  mutation DeleteApplicationAdGroup($input: DeleteApplicationAdGroupInput!) {
    resources {
      application {
        deleteApplicationAdGroup(input: $input) {
          applicationId
          message
          success
        }
      }
    }
  }
`;

/**
 * Represents a single role with its group-assigned responsibilities.
 * Used to cross-reference AD group names with application role assignments.
 */
export interface ApplicationRoleEntry {
  name: string;
  displayName: string;
  responsibilities: Array<{
    responsibilityId: number;
    responsibleParty: { name?: string } | null;
  }>;
}

/**
 * Query to get IAM SSO clients count for an application.
 * Used to count identity and access management SSO clients before retiring an application.
 */
export const GetIamClientsCountQuery = gql`
  query IamClientsCount($applicationId: Int!) {
    resources {
      application(applicationId: $applicationId) {
        resources {
          ssoClientsPaged {
            totalCount
          }
        }
      }
    }
  }
`;

/**
 * Query to get device counts for an application.
 * Used to count devices associated with the application.
 */
export const GetDeviceResourceCountsQuery = gql`
  query DevicesPaged(
    $applicationId: Int!
    $filter: ResourceCatalogDeviceFilter
  ) {
    resources {
      application(applicationId: $applicationId) {
        devicesPaged(filter: $filter) {
          totalCount
        }
      }
    }
  }
`;

/**
 * Query to get active (non-archived) repository counts for an application.
 * Used to count GitHub repositories associated with the application.
 */
export const GetRepositoryResourceCountsQuery = gql`
  query RepositoryResourceCounts(
    $applicationId: Int!
    $filter: ResourceCatalogGitHubRepositoryFilter
  ) {
    resources {
      application(applicationId: $applicationId) {
        sourceControl {
          github {
            repositoriesPaged(filter: $filter) {
              totalCount
            }
          }
        }
      }
    }
  }
`;

/**
 * Query to get components count for an application.
 * Note: Components are fetched separately as they use a different filter structure.
 */
export const GetApplicationComponentsCountQuery = gql`
  query ApplicationComponentsCount($applicationId: Int!) {
    resources {
      application(applicationId: $applicationId) {
        componentsPaged {
          totalCount
        }
      }
    }
  }
`;

/**
 * Combined query to get both SAT clients and SAT services count for an application.
 * Used to count Service Abstraction Toolkit resources before retiring an application.
 */
export const GetSatCountsQuery = gql`
  query SatCounts($applicationId: Int!) {
    resources {
      application(applicationId: $applicationId) {
        resources {
          satClientsPaged {
            totalCount
          }
          satServicesPaged {
            totalCount
          }
        }
      }
    }
  }
`;

/**
 * Response type for CNAP resources count fetch
 */
export interface CnapResourcesCountResult {
  count: number;
  errorOccurred: boolean;
}

/**
 * Fetches CNAP resources count for an application via REST API.
 * @param baseUrl - The base URL for the API
 * @param applicationId - The application ID to fetch CNAP resources for
 * @returns Promise with count and error status
 */
export const fetchCnapResourcesCount = async (
  baseUrl: string,
  applicationId: number,
): Promise<CnapResourcesCountResult> => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/cnap/cga-audit/${applicationId}`,
    );

    if (response.status === 200 && response.data?.data) {
      return {
        count: response.data.data.length || 0,
        errorOccurred: false,
      };
    }

    // No data in response means 0 count
    return { count: 0, errorOccurred: false };
  } catch (error) {
    // Handle 404 - means no CNAP resources exist for this application
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return { count: 0, errorOccurred: false };
    }

    // For other errors, report as error occurred
    return { count: 0, errorOccurred: true };
  }
};

/**
 * Type definitions for resource counts
 */
export interface ApplicationResourceCounts {
  cloudAccountsCount: number;
  devicesCount: number;
  adGroupsCount: number;
  iamClientsCount: number;
  githubReposCount: number;
  cnapResourcesCount: number;
  componentsCount: number;
  satClientsCount: number;
  satServicesCount: number;
}

/**
 * Type for tracking errors on individual resource types
 */
export type ResourceErrorState = Partial<
  Record<keyof ApplicationResourceCounts, boolean>
>;

/**
 * Initial state for resource errors (all false)
 */
export const initialResourceErrors: ResourceErrorState = {};

/**
 * Initial state for resource counts (all zeros while loading)
 */
export const initialResourceCounts: ApplicationResourceCounts = {
  cloudAccountsCount: 0,
  devicesCount: 0,
  adGroupsCount: 0,
  iamClientsCount: 0,
  githubReposCount: 0,
  cnapResourcesCount: 0,
  componentsCount: 0,
  satClientsCount: 0,
  satServicesCount: 0,
};

/**
 * Resource item type for display in checklist
 */
export interface ResourceChecklistItem {
  key: keyof ApplicationResourceCounts;
  label: string;
  count: number;
  icon: string;
  description: string;
  hasError?: boolean;
  /** If true, this resource must be 0 to retire. If false, it's informational only. */
  isMandatory: boolean;
  /** URL to navigate to for managing this resource */
  actionUrl: string;
  /** Label for the action button/link */
  actionLabel: string;
  /** If true, the link is external (opens in new tab). If false, it's a DevHub doc/page. */
  isExternal: boolean;
  /** Group identifier — every item belongs to a group for card rendering */
  groupId: string;
}

/**
 * Configuration for a resource group card.
 * Items with a matching groupId are rendered together under this group.
 */
export interface ResourceGroupConfig {
  /** Unique identifier matching ResourceChecklistItem.groupId */
  groupId: string;
  /** Display title for the grouped card */
  title: string;
  /** Icon name for the grouped card header */
  icon: string;
  /** Optional image source to use instead of icon */
  imageSrc?: string;
  /** Description shown in the grouped card */
  description: string;
  /** If true, show a delete button for the entire group (e.g. AD Groups) */
  allowDelete?: boolean;
}

/**
 * Registered resource groups.
 * Add new entries here when items need to be grouped under a single card.
 */
export const resourceGroups: ResourceGroupConfig[] = [
  {
    groupId: 'cloud-accounts',
    title: 'Active Cloud Accounts',
    icon: 'Cloud',
    imageSrc: '/icons/one-cloud-portal.png',
    description: 'AWS, Azure, GCP accounts connected to this application',
  },
  {
    groupId: 'devices',
    title: 'Devices',
    icon: 'Devices',
    imageSrc: '/icons/iop-next-gen.png',
    description: 'Servers and devices associated with this application',
  },
  {
    groupId: 'iam-clients',
    title: 'IAM SSO Active Clients',
    icon: 'VpnKey',
    imageSrc: '/icons/iam-sso.png',
    description: 'Identity and access management clients',
  },
  {
    groupId: 'ad-groups',
    title: 'AD Groups',
    icon: 'People',
    imageSrc: '/icons/azure.svg',
    description: 'Active Directory groups for access management',
    allowDelete: true,
  },
  {
    groupId: 'components',
    title: 'Components',
    icon: 'Extension',
    imageSrc: '/icons/devhub-black.svg',
    description: 'Software components associated with this application',
  },
  {
    groupId: 'cnap',
    title: 'CNAP Resources',
    icon: 'Storage',
    imageSrc: '/icons/cnap.svg',
    description: 'Cloud Native Application Platform resources',
  },
  {
    groupId: 'github-repos',
    title: 'GitHub Repositories',
    icon: 'GitHub',
    imageSrc: '/icons/github.svg',
    description: 'Source code repositories linked to this application',
  },
  {
    groupId: 'sat',
    title: 'SAT',
    icon: 'Security',
    imageSrc: '/icons/sat-ng.svg',
    description:
      'Service Access Token Service (SATS) clients and services connected to this application',
  },
];

/**
 * Create checklist items from resource counts
 * @param counts - The resource counts object
 * @param errors - Optional error state for each resource type
 * @returns Array of checklist items for display
 */
export const createChecklistItems = (
  counts: ApplicationResourceCounts,
  errors?: ResourceErrorState,
): ResourceChecklistItem[] => [
  // MANDATORY: Must be 0 to retire the application
  {
    key: 'cloudAccountsCount',
    label: 'Active Cloud Accounts',
    count: counts.cloudAccountsCount,
    icon: 'Cloud',
    description: 'AWS, Azure, GCP accounts connected to this application',
    hasError: errors?.cloudAccountsCount,
    isMandatory: true,
    actionUrl:
      'https://devhub.comcast.net/community/default/component/798bf5ceaebb21202dc1c3aa795ec98739b2f6bd/user-guides/changing-cloud-account-app-association/',
    actionLabel: 'Cloud account disassociation guide',
    isExternal: true,
    groupId: 'cloud-accounts',
  },
  {
    key: 'devicesCount',
    label: 'Devices',
    count: counts.devicesCount,
    icon: 'Devices',
    description: 'Servers and devices associated with this application',
    hasError: errors?.devicesCount,
    isMandatory: true,
    actionUrl: 'https://t.cmcst.net/6ysJk',
    actionLabel: 'Manage devices association',
    isExternal: true,
    groupId: 'devices',
  },
  {
    key: 'iamClientsCount',
    label: 'IAM SSO Active Clients',
    count: counts.iamClientsCount,
    icon: 'VpnKey',
    description: 'Identity and access management clients',
    hasError: errors?.iamClientsCount,
    isMandatory: true,
    actionUrl:
      'https://etwiki.sys.comcast.net/display/sso/How+to+Enable+Disabled+Application+from+IAM+Developer+Portal',
    actionLabel: 'IAM SSO management guide',
    isExternal: true,
    groupId: 'iam-clients',
  },
  // INFORMATIONAL: User has 30 days to clean up after retirement
  {
    key: 'adGroupsCount',
    label: 'AD Groups',
    count: counts.adGroupsCount,
    icon: 'People',
    description: 'Active Directory groups for access management',
    hasError: errors?.adGroupsCount,
    isMandatory: false,
    actionUrl: 'https://corpitsupport.comcast.com/ADGroups',
    actionLabel: 'Manage AD Groups',
    isExternal: true,
    groupId: 'ad-groups',
  },
  {
    key: 'componentsCount',
    label: 'Components',
    count: counts.componentsCount,
    icon: 'Extension',
    description: 'Software components associated with this application',
    hasError: errors?.componentsCount,
    isMandatory: false,
    actionUrl:
      'https://devhub.comcast.net/community/default/component/798bf5ceaebb21202dc1c3aa795ec98739b2f6bd/devhub/what_is_devhub_yaml/',
    actionLabel: 'Component management guide',
    isExternal: true,
    groupId: 'components',
  },
  {
    key: 'cnapResourcesCount',
    label: 'CNAP Resources',
    count: counts.cnapResourcesCount,
    icon: 'Storage',
    description: 'Cloud Native Application Platform resources',
    hasError: errors?.cnapResourcesCount,
    isMandatory: false,
    actionUrl: 'https://t.cmcst.net/4hrPF',
    actionLabel: 'CNAP Support Channel',
    isExternal: true,
    groupId: 'cnap',
  },
  {
    key: 'githubReposCount',
    label: 'GitHub Repositories',
    count: counts.githubReposCount,
    icon: 'GitHub',
    description: 'Source code repositories linked to this application',
    hasError: errors?.githubReposCount,
    isMandatory: false,
    actionUrl: 'https://t.cmcst.net/kGGeP',
    actionLabel: 'Repository archive guide',
    isExternal: true,
    groupId: 'github-repos',
  },
  {
    key: 'satClientsCount',
    label: 'SAT Clients',
    count: counts.satClientsCount,
    icon: 'Settings',
    description:
      'Service Abstraction Toolkit clients connected to this application',
    hasError: errors?.satClientsCount,
    isMandatory: false,
    actionUrl:
      'https://etwiki.sys.comcast.net/display/SATS/SAT+NG+Developer+Portal#SATNGDeveloperPortal-CreateNewClientID',
    actionLabel: 'SAT clients management guide',
    isExternal: true,
    groupId: 'sat',
  },
  {
    key: 'satServicesCount',
    label: 'SAT Services',
    count: counts.satServicesCount,
    icon: 'Settings',
    description:
      'Service Abstraction Toolkit services connected to this application',
    hasError: errors?.satServicesCount,
    isMandatory: false,
    actionUrl:
      'https://etwiki.sys.comcast.net/display/SATS/SAT+NG+Developer+Portal#SATNGDeveloperPortal-ServicesManagement',
    actionLabel: 'SAT services management guide',
    isExternal: true,
    groupId: 'sat',
  },
];
